import type { ProposedDraftChange } from '../../../api';
import { operationTargetsPath } from '../proposedChangePreview';

/** Root docs/index.md is shared, so it must not be accepted with a flat section. */
export function sectionDocumentPaths(
  section: { fields: { targetDocument?: string }[] },
  documents: { path: string }[],
): string[] {
  const paths = new Set<string>();
  for (const field of section.fields) {
    if (!field.targetDocument) continue;
    const path = field.targetDocument;
    const directory = path.slice(0, path.lastIndexOf('/'));
    const index = `${directory}/index.md`;
    if (
      directory.startsWith('docs/') &&
      documents.some(doc => doc.path === index)
    ) {
      paths.add(index);
    }
    paths.add(path);
  }
  return [...paths];
}

export function sectionPendingOperations(
  changes: ProposedDraftChange[],
  paths: string[],
) {
  return [...changes]
    .filter(change => change.status === 'pending')
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    )
    .flatMap(change =>
      change.operations.flatMap((operation, index) => {
        // A rename spanning sections must be reviewed separately, never silently accepted.
        const belongs =
          operation.type === 'renameFile'
            ? paths.includes(operation.from) && paths.includes(operation.to)
            : paths.some(path => operationTargetsPath(operation, path));
        return operation.status === 'pending' && belongs
          ? [{ changeId: change.id, operationIndex: index }]
          : [];
      }),
    );
}

export async function approveSectionOperations(
  changes: ProposedDraftChange[],
  paths: string[],
  approve: (
    id: string,
    index?: number,
    options?: { report?: boolean; throwOnError?: boolean },
  ) => Promise<void>,
) {
  for (const operation of sectionPendingOperations(changes, paths)) {
    await approve(operation.changeId, operation.operationIndex, {
      report: false,
      throwOnError: true,
    });
  }
}
